import { Component, Input, ViewEncapsulation, TemplateRef, ChangeDetectionStrategy} from '@angular/core';
import { IDoesFilterPassParams } from 'ag-grid-community';
import { IFilterAngularComp } from 'ag-grid-angular';
import { FormControl } from '@angular/forms';
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import { DropdownSearchMultipleGridLayoutService } from '@irb-common/components/dropdown-search-multiple/dropdown-search-multiple.grid-layout.service';
import { IDropdownItem } from '@irb-common/models/dropdown.model';
import { UtilityService } from '@irb/irb-common/services/utility.service';

@Component({ standalone: false,
  selector: 'irb-irb-ag-search-multiple-column-filter',
  templateUrl: './irb-ag-search-multiple-column-filter.component.html',
  styleUrls: ['./irb-ag-search-multiple-column-filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class IrbAgSearchMultipleColumnFilterComponent implements IFilterAngularComp {
  private params: any;
  inputControl = new FormControl();
  private justTextCleared: boolean = false;
  private _values: Array<IDropdownItem> = null;
  private originalValues: Array<IDropdownItem> = null;
  private appliedData: Array<IDropdownItem> = null;
  data: Array<IDropdownItem> = [];
  grid: any;
  gridOptions: any;
  gridHasFocus: boolean = false;
  isAllSelected: boolean = true;
  searchString: string = "";
  hilitedRowIndex: number;
  lastHilitedRowIndex: number;
  displayValue: string = "";
  showSearchResults: boolean = false;
  isVisible: boolean = false;
  search: Function;
  threshold: number = 0;
  field: string;
  displayField: string = "name";
  isUiTest = false;
  frameworkComponents: any;
  enableHiddenSearchResults: boolean = true;
  enableEmptySearchTerm: boolean = false;
  doesFilterPassCallback: Function;
  noOptionsTemplate: TemplateRef<any>;

  constructor(private gls: DropdownSearchMultipleGridLayoutService, private util: UtilityService) {}

  agInit(params: any): void {
    this.params = params;

    if(params.search) {
      this.search = params.search;
    } else {
      this.search = (s: string) => this.populateDropdown(s);
    }
    
    if(params.threshold) {
      this.threshold = params.threshold;
    }

    if(params.field) {
      this.field = params.field;
    }

    if(params.displayField) {
      this.displayField = params.displayField;
    }

    this.enableHiddenSearchResults = (typeof this.params.enableHiddenSearchResults !== "undefined") ? this.params.enableHiddenSearchResults : false;
    this.enableEmptySearchTerm = (typeof this.params.enableEmptySearchTerm !== "undefined") ? this.params.enableEmptySearchTerm : false;
    this.showSearchResults = (this.enableHiddenSearchResults) ? false : true;
    this.noOptionsTemplate = (typeof this.params.noOptionsTemplate !== "undefined") ? this.params.noOptionsTemplate : null;
    this.doesFilterPassCallback = (typeof this.params.doesFilterPassCallback !== "undefined") ? this.params.doesFilterPassCallback : null;

    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(),      
      suppressMovableColumns: true,
      gridAutoHeight: false,
      overlayLoadingTemplate: "<div class='loading-spinner'></div>",
      overlayNoRowsTemplate: "<span class='ag-overlay-loading-center ag-overlay-message'>No results found.</span>",
      rowSelection: "multiple",
      rowClassRules: {
        'ag-row-hilite': (params: any) => {
          const rowIndex = params.rowIndex || params.node.rowIndex;
          return rowIndex === this.hilitedRowIndex;
        }
      }
    };
    
    this.frameworkComponents = this.gls.getFrameworkComponents();

    this.inputControl.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
    ).subscribe(val => {
      if (!this.justTextCleared) {
        if (((this.values == null) ||
            (!this.valueExists(val))) && val != null) {
          this.onValueUpdate(val);
        }
      } else {
        this.justTextCleared = false;
      }
    });
  }

  afterGuiAttached() {
    this.inputControl.setValue('');
    this.onValueUpdate(this.inputControl.value);
    this.isVisible = true;
  }

  afterGuiDetached() {    
    this.isVisible = false;
  }

  @Input() set values(v: Array<IDropdownItem>) {
    this._values = (v) ? [].concat(v) : null;
    this.appliedData = (v) ? [].concat(v) : null;
  }

  get values(): Array<IDropdownItem> {
    return this._values;
  }

  valueExists(s: string): boolean {
    let b: boolean = false;

    this.values.forEach((v: IDropdownItem) => {
      if(v.name === s) {
        b = true;
      }
    });

    return b;
  }

  onValueUpdate(s: any) {
    if ((typeof s === "string" && s.length >= this.threshold) || this.enableEmptySearchTerm) {
      if(this.grid) {
        this.grid.setGridOption("rowData", []);
        this.grid.showLoadingOverlay();
      }

      this.searchString = s;
      this.search(s, this);
    } else {
      if(this.grid) {
        this.searchString = '';
        this.search(s, this);
      }
    }
  }

  setSearchResultVisibility() {
    const s = (typeof this.inputControl.value === "string") ? this.inputControl.value : "";
    this.showSearchResults = (this.enableHiddenSearchResults) ? s.length > this.threshold : true;
  }

  focusGrid() {
    if(this.grid && !this.gridHasFocus) {
      this.gridHasFocus = true;
      
      if(this.hilitedRowIndex === null) {
        this.hiliteRow(0);
      }      
    }
  }

  unFocusGrid() {
    this.gridHasFocus = false;
  }

  hiliteRow(index: number) {
    if(this.grid && typeof index !== "undefined") {
      if(index < 0) {
        index = 0;
      } else if(index > this.grid.getDisplayedRowCount() - 1) {
        index = this.grid.getDisplayedRowCount() - 1;
      }
  
      window.setTimeout(() => {
        if(index >= 0) {    
          const updatedNodes: any = [];     
          this.hilitedRowIndex = index;

          this.grid?.forEachNode((node: any) => {
            if(node.rowIndex === index || node.rowIndex === this.lastHilitedRowIndex) {
              updatedNodes.push(node);
            }
          });

          this.grid.redrawRows({rowNodes: updatedNodes});
          this.grid.ensureIndexVisible(index);       
          this.lastHilitedRowIndex = this.hilitedRowIndex;   
        }
      });
    }    
  }

  onGridReady(params: any) {    
    this.grid = params.api;     
    this.hilitedRowIndex = null;

    if(this.enableEmptySearchTerm) {
      this.onValueUpdate(this.inputControl.value);
    } else {
      this.set(this.data);
    }

    this.grid.hideOverlay();
  }

  set(m: Array<IDropdownItem>) {
    m = m || [];
    if (m.length !== 0) {
      m = m[0] ? m : [];
    }
    m = m.length !==0 ? m.filter((v: IDropdownItem) => v.value !== -1) : [];
    this.data = (this.values) ? this.values.filter((v: IDropdownItem) => v.value !== -1) || [] : [];

    if(m.length > 0) {
      if(this.values) {
        this.data = this.data.concat(m.filter((x: IDropdownItem) => 
          this.values.find((v: IDropdownItem) => (x.value === v.value)) === undefined
        ));
      } else {
        this.data = m;
      }
    }

    if (this.appliedData) {
      this.data = this.combineData();
    }
    
    this.data.forEach((obj: any) => { obj['match'] = this.searchString || ''; });

    if(this.grid) {
      if(this.data.length > 0 && this.data[0].value !== -1) {
        this.data.unshift({name: "(Select All)", value: -1, match: ""});
      }
      
      if(this.searchString?.length > 0 && this.data.length == 0) {
        this.grid?.setGridOption('overlayNoRowsTemplate', "<span class='ag-overlay-loading-center ag-overlay-message'>No items match your search for '" + this.searchString + "'.<br/><span style='color: #999'>Try searching for another term.</span>");
      }

      this.grid.setGridOption("rowData", this.data);
      this.selectDefaultRows();
    }
  }

  private combineData() {
    let isExists: Array<IDropdownItem>;
    const combineData: Array<IDropdownItem> = [].concat(this.appliedData);

    if (this.data.length !== 0) {
      this.data.forEach((d: any) => {
        isExists = combineData.filter((v: IDropdownItem) => v.value === d.value);
        if (isExists.length === 0) {
          combineData.push(d);
        }
      });
    }
    return combineData;
  }

  private selectDefaultRows() {
    if(this.appliedData) {
      this.grid?.forEachNode((node: any) => {
        this.appliedData.forEach((v: IDropdownItem) => {
          if(node.data.value === v.value) {
            this.selectRow(node.rowIndex);
          }
        });
      });
    }
  }

  selectRow(index: number) {
    if(this.grid && typeof index !== "undefined") {
      if(index < 0) {
        index = 0;
      } else if(index > this.grid.getDisplayedRowCount() - 1) {
        index = this.grid.getDisplayedRowCount() - 1;
      }

      if(index >= 0) {
        this.hilitedRowIndex = index;

        this.grid?.forEachNode((node: any) => {
          if(node.rowIndex === index) {
            node.setSelected(!node.isSelected());
            this.checkSelectAllChanged(node);
          }
        });  
        
        this.grid.redrawRows();
      }
    }
  }

  onRowClicked(event: any) {    
    this.selectRow(event.node.rowIndex);
    this.apply();
    this.focusGrid();
    this.hilitedRowIndex = event.node.rowIndex;
  }

  apply() {
    if(this.grid) {
      const data = this.grid.getSelectedRows().filter((x: IDropdownItem) => x.value !== -1);

      if (data.length > 0 || (data.length === 0)) {
        this.values = data;
        this.appliedData = [].concat(this.values);        
      }
    }
  }

  checkSelectAllChanged(node: any) {
    if(node.data.value === -1) {
      (node.isSelected()) ? this.selectAll() : this.deselectAll();
    } else {
      this.updateSelectAll();
    }
  }

  selectAll() {
    this.isAllSelected = true;

    this.grid?.forEachNode((node: any) => {
      node.setSelected(true);
    });
  }

  deselectAll() {
    this.isAllSelected = false;

    this.grid?.forEachNode((node: any) => {
      node.setSelected(false);
    });
  }

  updateSelectAll() {
    this.isAllSelected = true;

    this.grid?.forEachNode((node: any) => {
      if(node.data.value !== -1 && !node.isSelected()) {
        this.isAllSelected = false;
      }
    });

    this.grid.getDisplayedRowAtIndex(0).setSelected(this.isAllSelected);
  }

  hasChange() {
    if(this.grid) {
      const data = this.grid.getSelectedRows()?.filter((x: IDropdownItem) => x.value !== -1);

      if(data) {
        if(this.originalValues) {
          return !this.util.compare(this.originalValues, data, ['match'], false);
        } else {
          return data.length > 0;
        }
      } else {
        return false;
      }
    } else {
      return !this.util.compare(this.originalValues, this._values, [], false);
    }
  }

  getIDropdownItems(strArray: string[]) {
    const a  = this.data.filter((x: IDropdownItem) => strArray.includes(x.name));
    return a;
  }

  populateDropdown(searchString: string): void {
    if (searchString && searchString.length > 0) {
      this.set((this.params.values || [])
        .filter((item: any) => item.name &&
            item.name.toUpperCase().includes(searchString.toUpperCase()))
        );
    } else if(this.enableEmptySearchTerm) {
      this.set(this.params.values || []);
    }
  }

  /**
   * Custom Filter Interface START
   * ***************************/
  
  isFilterActive(): boolean {
    return (this.values) ? this.values.length > 0 : false;
  }

  doesFilterPass(_params: IDoesFilterPassParams): boolean {
    return (this.doesFilterPassCallback) ? this.doesFilterPassCallback(_params, this.values) : true;
  }

  getModelAsString(): any {
    if(this.isFilterActive()) {
      let a = [];

      for(let i = 0; i < this.values.length; i++) {
        a.push((this.displayField == "value") ? this.values[i].value : this.values[i].name); 
      }

      return "(" + this.values.length + ") " + a.join(",");
    } else {
      return null;
    }
  }

  getModelAsArray(useName?: boolean): any {
    if(this.isFilterActive()) {
      let a = [];

      for(let i = 0; i < this.values.length; i++) {
        if(useName) {
          a.push(this.values[i].name); 
        } else {
          a.push(this.values[i].value); 
        }
      }

      return a;
    } else {
      return null;
    }
  }

  getModel(): any {
    return this.isFilterActive() ? {
      values: this.getModelAsArray(),
      displayValues: this.getModelAsArray(true),
      filterType: (this.displayField == "value") ?  'set' : 'setByName',
      selectedItems: this.values
    } : null;
  }

  setModel(model: any): void {    
    // this.values = model ? this.getIDropdownItems(model.values) : [];
    if (model && model.values && model.values.length) {
      if (!isNaN(model.values[0])) {
        this.data = this.params.values || model.selectedItems;
        this.values = this.data.filter((x: IDropdownItem) => (model.values.includes(x.value)));
      } else {
        this.values = this.getIDropdownItems(model.values);
        if (!this.data.length && !this.values.length && !model.values[0].name) {
          this.data = model.selectedItems;
          this.values = model.selectedItems;
        }
        if (!this.values.length && this.data.length && model.selectedItems) {
          this.values = model.selectedItems;
        }
      }
    } else
      this.values = [];

    if (model === null) {
        this.clearAllValues();
    }
  }

  /**
   * Custom Filter Interface END
   * ***************************/

  onApplyAction(): void {
    this.apply();
    this.params.filterChangedCallback();
    this.isVisible = false;
  }

  onClearAction(): void {
    this.setModel(null);
    this.params.filterChangedCallback();
    this.isVisible = false;
  }

 clearAllValues(): void {
    this.justTextCleared = true;
    this.inputControl.setValue('');
    this.onValueUpdate(this.inputControl.value);
    this.searchString = '';
    this.setSearchResultVisibility();
  }
}
